import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi'
import { z } from 'zod'
import { CitedAgreementFactSchema } from '@omnea-app/packages/universal/task-agents/citations/agreementCitation'
import { CitedArtefactFactSchema } from '@omnea-app/packages/universal/task-agents/citations/supplierArtefactCitation'
import { captureAndReturn } from '@omnea-app/packages/universal/zod-utils/captureAndReturn'

extendZodWithOpenApi(z)

export const OverrideIconTypeSchema = z.enum(['bank', 'budget-approval', 'assignees'])
export type OverrideIconType = z.infer<typeof OverrideIconTypeSchema>

export const VisibleIfSchema = z
  .union([
    z.tuple([z.uuid()]), // [string]
    z.tuple([]), // []
  ])
  .catch(captureAndReturn('VisibleIfSchema'))

export type VisibleIf = z.infer<typeof VisibleIfSchema>

// Shared display fields every AI source carries; per-type members add their own identifiers.
const AiSourceBaseSchema = z.object({
  sourceLabel: z.string(),
  quote: z.string().optional(),
  sourceUrl: z.string().optional(),
  // Present only where the answer's own text cites the source by it, which a generated document's
  // `[1](#cite-<id>)` markers do and nothing else does. That makes the source a lookup table the text
  // points into rather than a claim about the answer, so it outlives an edit that invalidates the
  // claims. Read through `hasInlineCitations`.
  id: z.string().optional(),
})

// 'document' covers user-uploaded files, scraped URLs, and (for the agentic fill-in) document chunks
// surfaced by RAG search — everything backed by a filestore document. `sourceType` stays optional on
// this member only: rows written before the union predate the discriminator.
const DocumentAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('document').optional(),
  fileStoreId: z.string(),
  fileName: z.string().optional(),
  pageNumber: z.number().optional(),
  heading: z.string().optional(),
  sheetName: z.string().optional(),
})

// The remaining members are the agentic fill-in's non-document citation sources, mapped from the
// task agent review citation union at persist time. Each carries the click-through identifiers of its
// source; the filestore-backed ones keep the preview affordance via `fileStoreId`.
const PolicyAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('policy'),
  fileStoreId: z.string(),
  fileName: z.string().optional(),
  pageNumber: z.number().optional(),
  heading: z.string().optional(),
  policyId: z.string(),
  policyVersionId: z.string(),
})

const QuestionAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('question'),
  requestFormId: z.string(),
  questionName: z.string(),
})

const OtherRequestFormAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('other_request_form'),
  requestFormId: z.string(),
  questionName: z.string(),
})

const AgreementAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('agreement'),
  agreementId: z.string(),
  supplierId: z.string(),
  // Optional: older persisted rows only carry the facts flattened into `quote`.
  citedFacts: z.array(CitedAgreementFactSchema).min(1).optional(),
})

const TaskPrecedentAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('task_precedent'),
  requestId: z.string(),
  requestStepId: z.string(),
})

const ReviewHistoryAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('review_history'),
  requestId: z.string(),
  requestStepId: z.string(),
})

const DocumentReviewFindingAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('document_review_finding'),
  fileStoreId: z.string(),
  documentReviewId: z.string(),
})

// A public web page cited by the web-research slice. Has no filestore, so no preview affordance — the
// `sourceUrl` (required here, unlike the optional base field) drives an external open-link instead.
const UrlAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('url'),
  sourceUrl: z.string(),
  title: z.string().optional(),
})

const SupplierArtefactAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('supplier_artefact'),
  requestId: z.string(),
  requestArtefactId: z.string(),
  artefactTypeId: z.string(),
  citedFacts: z.array(CitedArtefactFactSchema).min(1),
})

// An intake answer on a similar past request; `requestId` is that past request, not the one on screen.
const SimilarRequestAiSourceSchema = z.object({
  ...AiSourceBaseSchema.shape,
  sourceType: z.literal('similar_request'),
  requestId: z.string(),
  requestFormId: z.string(),
  questionName: z.string(),
})

// Plain z.union, not discriminatedUnion: the document member's `sourceType` is optional (legacy rows),
// which a discriminated union cannot express. Document goes first so those legacy rows match it.
const AiSourceInfoSchema = z.union([
  DocumentAiSourceSchema,
  PolicyAiSourceSchema,
  QuestionAiSourceSchema,
  OtherRequestFormAiSourceSchema,
  AgreementAiSourceSchema,
  TaskPrecedentAiSourceSchema,
  ReviewHistoryAiSourceSchema,
  DocumentReviewFindingAiSourceSchema,
  UrlAiSourceSchema,
  SupplierArtefactAiSourceSchema,
  SimilarRequestAiSourceSchema,
])
export type AiSourceInfo = z.infer<typeof AiSourceInfoSchema>

export const AiCompletionConfidenceSchema = z.enum(['high', 'medium', 'low'])
export type AiCompletionConfidence = z.infer<typeof AiCompletionConfidenceSchema>

const AiCompletionReferenceSchema = z.object({
  title: z.string(),
  url: z.url(),
})

const AiCompletionMetadataSchema = z
  .object({
    confidence: AiCompletionConfidenceSchema,
    reasoning: z.string(),
    references: z.array(AiCompletionReferenceSchema).optional(),
  })
  .openapi({
    description:
      'Confidence and reasoning for an answer submitted by the AI Form Completion block. Distinct from aiSources, which tracks document-extraction prefill citations.',
  })

export const SubfieldAiMetadataSchema = z.object({
  isAiPrefilled: z.boolean().optional(),
  aiSources: z.array(AiSourceInfoSchema).optional(),
  confidence: AiCompletionConfidenceSchema.optional(),
})
export type SubfieldAiMetadata = z.infer<typeof SubfieldAiMetadataSchema>

// An interface, not z.infer: expanded into every form answer, `FormAnswerSchema` exceeds the TS7056 serialisation limit.
export interface FormAnswerMetadata {
  isAiPrefilled?: boolean
  aiSubfieldMetadata?: Record<string, SubfieldAiMetadata>
  aiSources?: AiSourceInfo[]
  aiCompletionMetadata?: z.infer<typeof AiCompletionMetadataSchema>
}

export const FormAnswerMetadataSchema: z.ZodType<FormAnswerMetadata> = z.object({
  isAiPrefilled: z.boolean().optional(),
  // Per-subfield AI provenance, keyed by a path into the answer's value: `${rowId}.${fieldKey}` for a
  // lineItems answer (double-entry confirm keys never appear — the base key stands for the pair). Only
  // subfields the AI authored are present; ones carried over from deterministic prefill or defaults are
  // absent. An absent map means answer-level semantics: every subfield shares the answer's provenance.
  // Keys stay plain strings — FormAnswerSchema parses leniently, and a strict key failing would nuke the
  // whole answer to its catch fallback.
  aiSubfieldMetadata: z.record(z.string(), SubfieldAiMetadataSchema).optional(),
  aiSources: z.array(AiSourceInfoSchema).optional(),
  aiCompletionMetadata: AiCompletionMetadataSchema.optional(),
})

export const BaseFormAnswerSchema = z.object({
  questionId: z.string().nullable(),
  metadata: FormAnswerMetadataSchema.optional(),
})
